import type { PropertyFilterOperatorExtended } from '@cloudscape-design/collection-hooks';
import type { ComponentType } from 'react';

import type { JSONObject } from '@/types/types';
import { EMPTY_VALUE } from '@/utils/collectionUtils';

import { CustomAttributeNumberInput } from '../renderers/field-layouts/CustomAttributeNumberInput';
import type { CustomAttributeProps } from '../renderers/field-layouts/CustomAttributeProps';
import { getBasicFieldConfig } from './defaults';
import { type FieldTypeConfig } from './types';

// matchToField only handles string JSON values, so number fields read the raw
// numeric value directly (preserving a legitimate 0)
const getNumericValue = (
  data: JSONObject | null | undefined,
  path: string
): number | null => {
  const value = data?.[path];

  return typeof value === 'number' ? value : null;
};

// Without an explicit `match` the engine coerces a blank field to '' via
// fixupFalsyValues, and `'' < '5'` is a true *string* comparison — every blank
// row would leak into < and <= results.
const numericComparisonOperators: PropertyFilterOperatorExtended<
  number | string | null
>[] = (['>', '<', '>=', '<='] as const).map((operator) => ({
  operator,
  match: (itemValue: unknown, tokenValue: number | string | null) => {
    const item = typeof itemValue === 'number' ? itemValue : null;
    // Trim first: Number('   ') is 0, which would act as a zero threshold.
    const trimmed =
      typeof tokenValue === 'string' ? tokenValue.trim() : tokenValue;
    const token = trimmed === null || trimmed === '' ? null : Number(trimmed);

    if (item === null || token === null || Number.isNaN(token)) {
      return false;
    }

    switch (operator) {
      case '>':
        return item > token;
      case '<':
        return item < token;
      case '>=':
        return item >= token;
      case '<=':
        return item <= token;
    }
  },
}));

export const number: FieldTypeConfig = {
  i18nKey: 'customAttributes.fieldTypes.number',
  FieldComponent:
    // TODO: Support generic value type
    CustomAttributeNumberInput as unknown as ComponentType<CustomAttributeProps>,
  getTableFieldConfig: (renderProps) => ({
    ...getBasicFieldConfig(renderProps),
    cell: (data) => {
      const value = getNumericValue(data.CustomAttributeData, renderProps.path);

      return value !== null ? String(value) : EMPTY_VALUE;
    },
    filterOptions: {
      filteringProperties: {
        // `operators` replaces DEFAULT_OPERATORS wholesale, so the inherited four
        // are repeated — dropping one silently breaks filters already saved
        // against it (the engine returns false for an unknown operator).
        operators: [':', '!:', '=', '!=', ...numericComparisonOperators],
      },
    },
    // Returns the raw number (not a formatted string) so the value hoisted onto
    // table records stays numeric — Smart Widget aggregation only sums values
    // where `typeof === 'number'`, and only offers numeric columns as a measure.
    // Must be null (not EMPTY_VALUE) for blanks, else a single empty row would
    // disqualify the whole column from the aggregate-field dropdown.
    customFieldValue: (item: { CustomAttributeData: JSONObject }) =>
      getNumericValue(item.CustomAttributeData, renderProps.path),
    exportVal: (item) => {
      const value = getNumericValue(item.CustomAttributeData, renderProps.path);

      return value ?? '';
    },
  }),
  getPdfExportValue: (renderProps, item) => {
    const value = getNumericValue(
      item.CustomAttributeData ?? null,
      renderProps.path
    );

    return value !== null ? String(value) : '';
  },
  getCustomDataSourceFieldDefinition: (renderProps) => ({
    defaultLabel: renderProps.label,
    displayType: 'number',
    dataType: 'number',
  }),
};
